import { formatDistanceToNow } from "date-fns";
import { ArrowRight, Building2, FileSearch, Loader2, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useActivePropertyTransactions } from "@/hooks/use-supabase-queries";
import { useAuth } from "@/contexts/AuthContext";
import { formatNaira } from "@/lib/format";
import { getDueDiligenceStartPath } from "@/lib/dueDiligence";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface ActivePropertyTransactionsCardProps {
  title?: string;
  emptyText?: string;
}

export function ActivePropertyTransactionsCard({
  title = "Property Transactions in Motion",
  emptyText = "No under-offer property transactions are active right now.",
}: ActivePropertyTransactionsCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: transactions, isLoading } = useActivePropertyTransactions();

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!transactions || transactions.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
            {emptyText}
          </div>
        ) : transactions.map((transaction: any) => {
          const requesterName = `${transaction.requester?.first_name || ""} ${transaction.requester?.last_name || ""}`.trim() || "Participant";
          const lawyerName = `${transaction.lawyer?.first_name || ""} ${transaction.lawyer?.last_name || ""}`.trim() || "Lawyer";
          const posterId = transaction.property?.seller_user_id || transaction.property?.listed_by || transaction.requester?.id;
          const conversationPartnerId = transaction.lawyer?.id === user?.id ? posterId : transaction.lawyer?.id;

          return (
            <div key={transaction.id} className="rounded-2xl border border-border bg-muted/20 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium text-foreground">{transaction.property?.title || "Property transaction"}</p>
                <Badge className="border-warning/20 bg-warning/10 text-warning">Under Offer</Badge>
              </div>
              <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" />
                <span>{transaction.property?.address}, {transaction.property?.state}</span>
              </div>
              <div className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                <p>Requester: {requesterName}</p>
                <p>Lawyer: {lawyerName}</p>
                <p>Value: {transaction.property?.price ? formatNaira(transaction.property.price) : "TBD"}</p>
                <p>Activated {formatDistanceToNow(new Date(transaction.reviewed_at || transaction.created_at), { addSuffix: true })}</p>
              </div>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <Button variant="outline" className="flex-1" onClick={() => navigate("/properties")}>
                  View property
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => navigate(
                    transaction.lawyer?.id === user?.id
                      ? getDueDiligenceStartPath(transaction)
                      : (conversationPartnerId ? `/messages?contact=${conversationPartnerId}` : "/messages"),
                  )}
                >
                  {transaction.lawyer?.id === user?.id ? (
                    <>
                      Start due diligence
                      <FileSearch className="ml-2 h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Open conversation
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
